/**
 * A category or specialism leaderboard. Both are the same page: the taxonomy is
 * two levels deep and a specialism board is a narrower scope.
 */

import { Link, useParams } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';

import {
  MINIMUM_FIRST_BID_CENTS,
  PASS_INCREMENT_CENTS,
  getCategory,
  getSubcategory,
  type LeaderboardScope,
} from '@bidtohire/shared';

import { BoardSection } from '../components/BoardSection';
import { SubcategoryChips } from '../components/CategoryNav';
import { Money } from '../components/Money';
import { PUBLISH_FROM_LABEL, firstPlaceTotalCents } from '../lib/bid-pricing';
import { boardSalaryStat, compactMoneyLabel } from '../lib/board-stats';
import { useLeaderboard } from '../lib/queries';
import { NotFoundPage } from './NotFoundPage';

export function ScopePage(): ReactNode {
  const params = useParams<{ category: string; subcategory?: string }>();
  const categorySlug = params.category ?? '';
  const subcategorySlug = params.subcategory;

  const category = getCategory(categorySlug);
  const subcategory =
    subcategorySlug === undefined ? undefined : getSubcategory(categorySlug, subcategorySlug);

  const scope: LeaderboardScope =
    category === undefined
      ? { kind: 'global' }
      : subcategory === undefined
        ? { kind: 'category', category: category.slug }
        : { kind: 'subcategory', category: category.slug, subcategory: subcategory.slug };

  // The board is fetched here as well as inside the section; TanStack shares
  // the cache entry, so the header figures cost nothing extra.
  const board = useLeaderboard(scope);
  const entries = useMemo(
    () => (board.data?.pages ?? []).flatMap((page) => page.items),
    [board.data],
  );

  if (category === undefined) return <NotFoundPage />;
  if (subcategorySlug !== undefined && subcategory === undefined) return <NotFoundPage />;

  const scopeName = subcategory === undefined ? category.name : subcategory.name;
  // A failed or unfinished read has no figures to report; printing the
  // fallbacks would state "top spot $5" as a fact about a board nobody read.
  const figuresKnown = !board.isError && !board.isLoading && board.data !== undefined;
  const totalListings = board.data?.pages[0]?.totalListings ?? entries.length;
  const topSpot = firstPlaceTotalCents(entries.map((entry) => entry.totalPaidCents));
  const leaderTotal = entries[0]?.totalPaidCents ?? 0;
  const salaryStat = boardSalaryStat(entries);
  const isEmpty = figuresKnown && totalListings === 0;

  return (
    <>
      <div
        style={{
          position: 'relative',
          borderBottom: '1px solid var(--hairline)',
          background: 'linear-gradient(180deg,#f9fbfd,#ffffff)',
        }}
      >
        <div className="inner" style={{ paddingTop: 26, paddingBottom: 26 }}>
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Leaderboard</Link>
            <span className="sep">/</span>
            {subcategory === undefined ? (
              <span>{category.name}</span>
            ) : (
              <>
                <Link to={`/c/${category.slug}`}>{category.name}</Link>
                <span className="sep">/</span>
                <span>{subcategory.name}</span>
              </>
            )}
          </nav>

          <div
            className="row"
            style={{ alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 14 }}
          >
            <div>
              <h1 style={{ fontSize: 32 }}>{scopeName}</h1>
              <p
                className="muted"
                style={{ marginTop: 8, fontSize: 15, maxWidth: '62ch', letterSpacing: '-0.008em' }}
              >
                {isEmpty ? (
                  <>
                    No listings in{' '}
                    {subcategory === undefined
                      ? category.name
                      : `${category.name} → ${subcategory.name}`}{' '}
                    yet. A first bid of <Money cents={MINIMUM_FIRST_BID_CENTS} /> puts a role here
                    and takes the top spot.
                  </>
                ) : (
                  <>
                    {totalListings.toLocaleString('en-US')}{' '}
                    {totalListings === 1 ? 'listing' : 'listings'} in{' '}
                    {subcategory === undefined
                      ? category.name
                      : `${category.name} → ${subcategory.name}`}
                    , ranked by the total each company has paid.
                  </>
                )}
              </p>
            </div>

            {/*
              The label deliberately omits the scope: the heading directly above
              already names it, and a category like "Product & Project
              Management" made the pill wider than a phone viewport. A fixed
              label fits every board at every width.
            */}
            <div className="row" style={{ gap: 10 }}>
              <Link className="pill pill-primary" to="/post">
                Post a role — {PUBLISH_FROM_LABEL}
              </Link>
            </div>
          </div>

          <div className="statgrid is-three" style={{ marginTop: 26 }}>
            <div className="stat">
              <span className="eyebrow">Top spot costs</span>
              <div className="value money" style={{ color: 'var(--indigo-deep)' }}>
                {figuresKnown ? <Money cents={topSpot} /> : '—'}
              </div>
              <div className="note">
                {!figuresKnown ? (
                  'Reading the board…'
                ) : leaderTotal > 0 ? (
                  <>
                    The leader’s <Money cents={leaderTotal} /> +{' '}
                    <Money cents={PASS_INCREMENT_CENTS} />
                  </>
                ) : (
                  <>
                    No bids here yet. The floor is <Money cents={MINIMUM_FIRST_BID_CENTS} />
                  </>
                )}
              </div>
            </div>
            <div className="stat">
              <span className="eyebrow">Active listings</span>
              <div className="value money">
                {figuresKnown ? totalListings.toLocaleString('en-US') : '—'}
              </div>
              <div className="note">
                {isEmpty ? 'Nothing listed here yet' : 'Ranked by the total paid on each'}
              </div>
            </div>
            {/*
              The money a reader cares about on a board is the money on offer,
              not what the board has taken: what one company paid for one
              position is stated on that listing's own row, where it means
              something.
            */}
            {salaryStat.kind === 'median' ? (
              <div className="stat">
                <span className="eyebrow">Median salary</span>
                <div className="value money">{compactMoneyLabel(salaryStat.cents)}</div>
                <div className="note">Across the listings shown</div>
              </div>
            ) : (
              <div className="stat">
                <span className="eyebrow">Companies hiring</span>
                <div className="value money">{salaryStat.count.toLocaleString('en-US')}</div>
                <div className="note">Across the listings shown</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="inner" style={{ paddingTop: 24 }}>
        <SubcategoryChips category={category.slug} active={subcategory?.slug} />
      </div>

      <section className="section is-last" style={{ paddingTop: 20 }}>
        <div className="inner">
          <BoardSection
            scope={scope}
            scopeName={scopeName}
            footNote={
              <>
                Bid any amount from <Money cents={MINIMUM_FIRST_BID_CENTS} />. Rank is the total
                paid, so passing any listing takes its total{' '}
                <span className="mono">
                  + <Money cents={PASS_INCREMENT_CENTS} />
                </span>
                . Ties keep the earlier bid ahead, so a row must be exceeded, not matched.
              </>
            }
          />
        </div>
      </section>
    </>
  );
}
